import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { DateTimePicker, LocalizationProvider, pickersLayoutClasses } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ButtonGroup, Button } from '@mui/material';
import MDBox from 'components/MDBox';

const PRESETS = {
  today: {
    label: 'Today',
    getRange: () => ({
      startDate: dayjs().startOf('day'),
      endDate: dayjs().endOf('day')
    })
  },
  yesterday: {
    label: 'Yesterday',
    getRange: () => ({
      startDate: dayjs().subtract(1, 'day').startOf('day'),
      endDate: dayjs().subtract(1, 'day').endOf('day')
    })
  },
  weekly: {
    label: 'Weekly',
    getRange: () => ({
      startDate: dayjs().startOf('week'),
      endDate: dayjs().endOf('day')
    })
  },
  monthly: {
    label: 'Monthly',
    getRange: () => ({
      startDate: dayjs().startOf('month'),
      endDate: dayjs().endOf('day')
    })
  },
  yearly: {
    label: 'Yearly',
    getRange: () => ({
      startDate: dayjs().subtract(1, 'year'),
      endDate: dayjs().endOf('day')
    })
  },
  ytd: {
    label: 'YTD',
    getRange: () => ({
      startDate: dayjs().startOf('year'),
      endDate: dayjs().endOf('day')
    })
  },
  alltime: {
    label: 'All Time',
    getRange: () => ({
      startDate: null,
      endDate: null
    })
  },
  custom: {
    label: 'Custom',
    getRange: () => null
  }
};

function DateFilterPresets({
  onDateChange,
  preset = 'today',
  startDate: externalStartDate,
  endDate: externalEndDate,
  showCustomPickers = true
}) {
  const [activePreset, setActivePreset] = useState(preset);
  const [customFrom, setCustomFrom] = useState(externalStartDate ? dayjs(externalStartDate) : dayjs().startOf('day'));
  const [customTo, setCustomTo] = useState(externalEndDate ? dayjs(externalEndDate) : dayjs().endOf('day'));

  useEffect(() => {
    setActivePreset(preset);
  }, [preset]);

  useEffect(() => {
    if (externalStartDate) {
      setCustomFrom(dayjs(externalStartDate));
    }
    if (externalEndDate) {
      setCustomTo(dayjs(externalEndDate));
    }
  }, [externalStartDate, externalEndDate]);

  const handlePresetClick = (presetKey) => {
    setActivePreset(presetKey);

    if (presetKey === 'custom') {
      return;
    }

    const range = PRESETS[presetKey].getRange();
    if (range && onDateChange) {
      if (range.startDate === null && range.endDate === null) {
        setCustomFrom(null);
        setCustomTo(null);
        onDateChange(null, null, presetKey);
      } else {
        setCustomFrom(range.startDate);
        setCustomTo(range.endDate);
        onDateChange(range.startDate.toDate(), range.endDate.toDate(), presetKey);
      }
    }
  };

  const handleCustomFromChange = (value) => {
    if (!value) return;

    let newFrom = value;
    let newTo = customTo;

    if (customTo && value.isAfter(customTo)) {
      newFrom = customTo;
      newTo = value;
    }

    setCustomFrom(newFrom);
    setCustomTo(newTo);
    setActivePreset('custom');

    if (onDateChange && newFrom && newTo) {
      onDateChange(newFrom.toDate(), newTo.toDate());
    }
  };

  const handleCustomToChange = (value) => {
    if (!value) return;

    let newFrom = customFrom;
    let newTo = value;

    if (customFrom && value.isBefore(customFrom)) {
      newFrom = value;
      newTo = customFrom;
    }

    setCustomFrom(newFrom);
    setCustomTo(newTo);
    setActivePreset('custom');

    if (onDateChange && newFrom && newTo) {
      onDateChange(newFrom.toDate(), newTo.toDate());
    }
  };

  return (
    <MDBox sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {Object.entries(PRESETS).map(([key, preset]) => (
          <Button
            key={key}
            onClick={() => handlePresetClick(key)}
            variant={activePreset === key ? 'contained' : 'outlined'}
            color='primary'
            size='small'
            sx={{ borderRadius: '8px', fontSize: { xs: '0.7rem', sm: '0.75rem' }, px: { xs: 1, sm: 1.25 } }}
          >
            {preset.label}
          </Button>
        ))}
      </MDBox>

      {showCustomPickers && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MDBox sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <DateTimePicker
              label='From'
              value={activePreset === 'alltime' ? null : customFrom}
              onChange={handleCustomFromChange}
              disabled={activePreset === 'alltime'}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
                  placeholder: activePreset === 'alltime' ? 'No filter' : undefined,
                  InputProps: activePreset === 'alltime' ? { inputProps: { value: '' } } : undefined
                },
                layout: {
                  sx: {
                    [`.${pickersLayoutClasses.actionBar}`]: {
                      display: 'none'
                    }
                  }
                }
              }}
              ampm={false}
              closeOnSelect
            />
            <DateTimePicker
              label='To'
              value={activePreset === 'alltime' ? null : customTo}
              onChange={handleCustomToChange}
              disabled={activePreset === 'alltime'}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
                  placeholder: activePreset === 'alltime' ? 'No filter' : undefined,
                  InputProps: activePreset === 'alltime' ? { inputProps: { value: '' } } : undefined
                },
                layout: {
                  sx: {
                    [`.${pickersLayoutClasses.actionBar}`]: {
                      display: 'none'
                    }
                  }
                }
              }}
              ampm={false}
              closeOnSelect
            />
          </MDBox>
        </LocalizationProvider>
      )}
    </MDBox>
  );
}

DateFilterPresets.defaultProps = {
  preset: 'today',
  showCustomPickers: true,
  startDate: null,
  endDate: null
};

DateFilterPresets.propTypes = {
  onDateChange: PropTypes.func.isRequired,
  preset: PropTypes.oneOf(['today', 'yesterday', 'weekly', 'monthly', 'yearly', 'ytd', 'alltime', 'custom']),
  showCustomPickers: PropTypes.bool,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date)
};

export default DateFilterPresets;
